import React from "react";
import Layout from "@/components/layout/Layout";
import MainHeading from "@/components/common/MainHeading";
import Card from "@/components/common/Card";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import { Badge } from "@/components/ui/badge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

import {
    Eye,
    MoreHorizontal,
    Wallet,
    Lock,
    Building2,
    CircleDollarSign,
    TrendingUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const Payslip: React.FC = () => {
    const navigate = useNavigate()
    const columns: TableColumn[] = [
        {
            key: "name",
            header: "Office Name",
            render: () => (
                <div>
                    <p className="font-semibold text-slate-800">
                        Sadguru Medical Trust
                    </p>
                    <p className="text-xs text-slate-500">Nagpur</p>
                </div>
            ),
        },
        {
            key: "attendanceLockStatus",
            header: "Attendance",
            render: () => (
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 rounded-full px-3">
                    Locked
                </Badge>
            ),
        },
        {
            key: "salaryStatus",
            header: "Salary Status",
            render: () => (
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 rounded-full px-3">
                    Processed
                </Badge>
            ),
        },
        {
            key: "action",
            header: "Action",
            render: () => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            className="h-9 w-9 rounded-full hover:bg-slate-100"
                        >
                            <MoreHorizontal size={18} />
                        </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate("/payslip/detail")}>
                            <Eye className="mr-2 h-4 w-4" />
                            View Payslip
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ];

    const data = [
        {},
        {},
        {},
    ];

    const stats = [
        {
            title: "Total Payslips",
            value: "24",
            icon: Wallet,
            bg: "from-blue-500 to-cyan-500",
        },
        {
            title: "Attendance Locked",
            value: "24",
            icon: Lock,
            bg: "from-green-500 to-emerald-500",
        },
        {
            title: "Organizations",
            value: "01",
            icon: Building2,
            bg: "from-violet-500 to-indigo-500",
        },
        {
            title: "Salary Processed",
            value: "100%",
            icon: CircleDollarSign,
            bg: "from-orange-500 to-amber-500",
        },
    ];

    return (
        <Layout>
            <div className="space-y-6">

                <MainHeading title="My Payslip" />

                {/* Header Banner */}

                <Card className="overflow-hidden border-0 bg-linear-to-r from-primary/90 via-primary to-primary/90 text-white shadow-xl">

                    <div className="flex flex-col lg:flex-row justify-between items-center p-8">

                        <div>

                            <h2 className="text-3xl font-bold">
                                Payroll Dashboard
                            </h2>

                            <p className="mt-2 text-blue-100 max-w-xl">
                                View salary slips, attendance status and payroll history in one
                                place.
                            </p>

                        </div>

                        <div className="hidden lg:flex h-24 w-24 rounded-full bg-white/20 items-center justify-center">

                            <TrendingUp size={42} />

                        </div>

                    </div>

                </Card>

                {/* Summary */}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">

                    {stats.map((item) => {
                        const Icon = item.icon;

                        return (
                            <Card
                                key={item.title}
                                className="relative overflow-hidden border-0 shadow-md hover:shadow-xl transition-all duration-300"
                            >
                                <div
                                    className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${item.bg}`}
                                />

                                <div className="flex justify-between items-center">

                                    <div>

                                        <p className="text-sm text-slate-500">
                                            {item.title}
                                        </p>

                                        <h2 className="mt-2 text-3xl font-bold">
                                            {item.value}
                                        </h2>

                                    </div>

                                    <div
                                        className={`h-14 w-14 rounded-xl bg-gradient-to-r ${item.bg} flex items-center justify-center text-white shadow-lg`}
                                    >
                                        <Icon size={26} />
                                    </div>

                                </div>

                            </Card>
                        );
                    })}

                </div>

                {/* Table */}

                <Card className="border-0 shadow-md">

                    <div className="mb-5">

                        <h3 className="text-lg font-semibold">
                            Payslip History
                        </h3>

                        <p className="text-sm text-slate-500">
                            List of processed salary slips.
                        </p>

                    </div>

                    <CommonTable
                        columns={columns}
                        data={data}
                    />

                </Card>

            </div>
        </Layout>
    );
};

export default Payslip;